// Comparing teams on a Stats tile or a Radar (Ben, 2026-11-01), the way players are compared: a
// team dropped on one showing a team joins it (a League Table row, a Match card name, a Squad's
// club), and so does another of the same kind showing a team (it's absorbed). Each team keeps
// its own league and season, so leagues mix. A chip's ✕ takes a team off; down to one, the tile
// is a plain team tile again. The same ⚡ shake and burst as a player merge.
//
// State on the tile: `_team` (the first, so everything that asks "which team?" still works),
// `_mergedTeams` (every team, the first included) and `_mergedTeamColors` (a colour each, which
// stays with its team).

import type { TeamRef } from "../live/team.js";
import { teamOfModule } from "../live/team.js";
import { getModuleConfigSync } from "../modules/module-config.js";
import { getModState, setModState, showInsteadOfWidget } from "../modules/bridge.js";
import { canvasApi } from "./shell-actions.js";
import { undoApi } from "./undo.js";
import { absorb } from "./drop-actions.js";
import { FIRST_PAIR_COLORS, chargingHtml, nextCompareColor } from "./merge-rules.js";
import { joinedTeams, teamJoinDecision } from "./team-merge-rules.js";

const titleOf = (mod: Element) => mod.querySelector(".module-title")?.textContent?.trim() ?? "";

/** The modules that compare teams: the Stats tiles and the Radar (2026-11-02). */
const COMPARES_TEAMS: ReadonlySet<string> = new Set(["renderStatsModule", "renderRadarModule"]);

/** A Stats tile's or Radar's team, when it shows one (else null). */
function statsTeamOf(mod: Element | null): TeamRef | null {
  const renderer = mod ? getModuleConfigSync(titleOf(mod))?.renderer : undefined;
  if (!mod || renderer === undefined || !COMPARES_TEAMS.has(renderer)) return null;
  return teamOfModule(mod);
}

/** Is this a Stats tile or Radar showing a team (or teams)? A player can't join it. */
export function statsTileShowsTeam(mod: Element | null): boolean {
  return statsTeamOf(mod) !== null;
}

/** The teams a tile compares (two or more), or null. */
export function mergedTeamsOf(mod: Element): TeamRef[] | null {
  const merged = getModState<TeamRef[]>(mod as HTMLElement, "_mergedTeams");
  return merged && merged.length > 1 ? merged : null;
}

function membersOf(mod: Element): TeamRef[] {
  const team = statsTeamOf(mod);
  if (!team) return [];
  return mergedTeamsOf(mod) ?? [team];
}

/** Does `team`, dropped on `target`, join it (a Stats tile showing a team, with room)? */
export function teamJoinsTile(target: Element | null, team: TeamRef): boolean {
  return !!target && statsTeamOf(target) !== null && teamJoinDecision(membersOf(target), team) === "join";
}

/** A Stats tile showing a team that turns `team` away (already on it, or full): red. */
export function teamRefusedBy(target: Element | null, team: TeamRef): boolean {
  return statsTeamOf(target) !== null && !teamJoinsTile(target, team);
}

/** Does the module show one player (its picker set, no comparison, no team)? */
function lonePlayerOf(mod: HTMLElement): boolean {
  const select = mod.querySelector<HTMLSelectElement>(".player-select");
  if (!select || select.value === "" || teamOfModule(mod) !== null) return false;
  return (getModState<unknown[]>(mod, "_mergedPlayers")?.length ?? 0) < 2;
}

/** A tile dropped on a tile, when either is a Stats tile or Radar showing a team: "team-merge" when
 *  the two combine (same tile, both teams); "switch" when a module showing one player lands on
 *  one showing teams (the player replaces them, Ben 2026-11-02, as a team replaces a player); null
 *  when they don't (red, never a player merge of the players underneath); undefined when neither
 *  shows a team (the usual rules decide). */
export function teamModuleDrop(source: HTMLElement, target: HTMLElement): "team-merge" | "switch" | null | undefined {
  const from = statsTeamOf(source);
  const to = statsTeamOf(target);
  if (!from && !to) return undefined;
  if (!from && to) return lonePlayerOf(source) ? "switch" : null;
  if (!from || !to || source === target || titleOf(source) !== titleOf(target)) return null;
  // A comparison dropped on a tile would bring several teams; one at a time, as players.
  if (mergedTeamsOf(source)) return null;
  return teamJoinDecision(membersOf(target), from) === "join" ? "team-merge" : null;
}

/** Shake the tile(s) with the ⚡ names, then add `team` as the burst starts. */
function charge(target: HTMLElement, team: TeamRef, source: HTMLElement | null): void {
  const members = membersOf(target);
  const html = chargingHtml([...members, team].map((t) => t.name));
  for (const mod of source ? [target, source] : [target]) {
    const body = mod.querySelector<HTMLElement>(".module-body");
    if (body) showInsteadOfWidget(body, html);
    mod.classList.add("merge-shaking");
  }
  target.addEventListener(
    "animationend",
    () => {
      source?.classList.remove("merge-shaking");
      target.classList.remove("merge-shaking");
      const first = statsTeamOf(target);
      if (!first) throw new Error("team merge: the tile lost its team");
      const colors = getModState<string[]>(target, "_mergedTeamColors");
      const merged = mergedTeamsOf(target);
      setModState(target, "_mergedTeams", joinedTeams(first, merged ?? undefined, team));
      setModState(target, "_mergedTeamColors", merged && colors ? [...colors, nextCompareColor(colors)] : [...FIRST_PAIR_COLORS]);
      canvasApi().refreshModuleBody(target);
      target.classList.add("merge-bursting");
      target.addEventListener("animationend", () => target.classList.remove("merge-bursting"), { once: true });
      if (source) absorb(source);
    },
    { once: true },
  );
}

/** A team (a token, a Squad's club) joins the tile. Holds no Undo step: the drag took it. */
export function joinTeam(target: HTMLElement, team: TeamRef): void {
  charge(target, team, null);
}

/** A Stats tile showing a team, dropped on one of the same kind: its team joins, it goes. */
export function mergeTeamModules(source: HTMLElement, target: HTMLElement): void {
  const team = statsTeamOf(source);
  if (!team) throw new Error("team merge: the dragged tile shows no team");
  // Collide at one point, at the target's size (as a player merge).
  source.style.left = target.style.left;
  source.style.top = target.style.top;
  source.style.width = target.style.width;
  source.style.height = target.style.height;
  source.style.zIndex = "51";
  charge(target, team, source);
}

/** A chip's ✕: that team goes. Down to one, the tile shows it alone. */
export function unmergeTeam(mod: HTMLElement, idx: number): void {
  const merged = mergedTeamsOf(mod);
  if (!merged) return;
  undoApi().saveState();
  const teams = merged.filter((_, i) => i !== idx);
  const colors = (getModState<string[]>(mod, "_mergedTeamColors") ?? []).filter((_, i) => i !== idx);
  setModState(mod, "_team", teams[0]);
  if (teams.length > 1) {
    setModState(mod, "_mergedTeams", teams);
    setModState(mod, "_mergedTeamColors", colors);
  } else {
    setModState(mod, "_mergedTeams", undefined);
    setModState(mod, "_mergedTeamColors", undefined);
  }
  canvasApi().refreshModuleBody(mod);
  const box = mod.querySelector<HTMLInputElement>(".player-search");
  if (box && teams[0]) box.value = teams[0].name;
}
